import React from 'react';
import { CheckCircle, Star, Play, Settings, Users, BookOpen, Brain, Calculator, Microscope, Globe } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import { useNavigate } from 'react-router-dom';

interface CompletionStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

const subjectIcons: Record<string, React.ReactNode> = {
  math: <Calculator className="w-5 h-5" />,
  reading: <BookOpen className="w-5 h-5" />,
  science: <Microscope className="w-5 h-5" />,
  social: <Globe className="w-5 h-5" />,
  focus: <Brain className="w-5 h-5" />
};

const subjectNames: Record<string, string> = {
  math: 'Math',
  reading: 'Reading & Language',
  science: 'Science',
  social: 'Social Studies',
  focus: 'Focus & Attention'
};

const subjectColors: Record<string, string> = {
  math: 'blue',
  reading: 'green',
  science: 'purple',
  social: 'orange',
  focus: 'pink'
};

export function CompletionStep({ wizardData }: CompletionStepProps) {
  const navigate = useNavigate();
  const currentChild = wizardData.createdChild || wizardData.selectedChild;
  const completedActivities = wizardData.generatedActivities.filter(a => a.status === 'completed');
  const failedActivities = wizardData.generatedActivities.filter(a => a.status === 'error');

  const handleStartLearning = () => {
    if (currentChild) {
      navigate(`/child/${currentChild.id}/dashboard`);
    }
  };

  const handleManageChild = () => {
    navigate('/adult/children');
  };

  const handleViewActivities = () => {
    navigate('/adult/activities');
  };

  const handleCreateMore = () => {
    window.location.reload(); // Restart the wizard
  };

  const getSubjectSummary = () => {
    const subjectCounts: Record<string, number> = {};
    completedActivities.forEach(activity => {
      subjectCounts[activity.subject] = (subjectCounts[activity.subject] || 0) + 1;
    });
    return subjectCounts;
  };

  const subjectSummary = getSubjectSummary();

  return (
    <div className="space-y-8">
      {/* Success Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          🎉 Activities Generated Successfully!
        </h3>
        <p className="text-gray-600 text-lg">
          {completedActivities.length} personalized learning activities are ready for {currentChild?.name || 'your child'}
        </p>
      </div>

      {/* Generation Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          What We Created
        </h4>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Child Info */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3">Child Profile</h5>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: currentChild?.avatar_color || '#3B82F6' }}
              >
                {currentChild?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-gray-900">{currentChild?.name}</div>
                <div className="text-sm text-gray-600">Age {currentChild?.age}</div>
                {wizardData.mode === 'new-child' && (
                  <div className="text-xs text-green-600">✨ Newly created</div>
                )}
              </div>
            </div>
          </div>

          {/* Activity Breakdown */}
          <div>
            <h5 className="font-semibold text-gray-900 mb-3">Activities by Subject</h5>
            <div className="space-y-2">
              {Object.entries(subjectSummary).map(([subject, count]) => (
                <div key={subject} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <div className={`p-1 rounded bg-${subjectColors[subject]}-100`}>
                      {subjectIcons[subject]}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{subjectNames[subject]}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Learning Profile Summary */}
        {wizardData.learningProfile && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h5 className="font-semibold text-gray-900 mb-3">Personalization Applied</h5>
            <div className="grid md:grid-cols-3 gap-4">
              {wizardData.learningProfile.learningObjectives.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Learning Objectives</div>
                  <div className="text-sm text-gray-600">
                    {wizardData.learningProfile.learningObjectives.length} custom objectives
                  </div>
                </div>
              )}
              {wizardData.learningProfile.accommodations.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Accommodations</div>
                  <div className="text-sm text-gray-600">
                    {wizardData.learningProfile.accommodations.length} accommodations applied
                  </div>
                </div>
              )}
              {wizardData.learningProfile.customInstructions && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Custom Instructions</div>
                  <div className="text-sm text-gray-600">Applied to all activities</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Failure Notice (if any) */}
      {failedActivities.length > 0 && (
        <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 text-sm font-bold">!</span>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {failedActivities.length} activities couldn't be generated
              </div>
              <div className="text-xs text-gray-600">
                Don't worry! You can try generating more activities anytime from the parent dashboard.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          What's Next?
        </h4>
        
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={handleStartLearning}
            className="flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105"
          >
            <Play className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">Start Learning</div>
              <div className="text-sm opacity-90">Jump into the activities</div>
            </div>
          </button>

          <button
            onClick={handleManageChild}
            className="flex items-center justify-center space-x-3 p-4 bg-white border-2 border-purple-200 text-purple-700 rounded-xl hover:bg-purple-50 transition-all"
          >
            <Users className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">Manage Profile</div>
              <div className="text-sm opacity-75">View child dashboard</div>
            </div>
          </button>

          <button
            onClick={handleViewActivities}
            className="flex items-center justify-center space-x-3 p-4 bg-white border-2 border-blue-200 text-blue-700 rounded-xl hover:bg-blue-50 transition-all"
          >
            <Settings className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">Activity Manager</div>
              <div className="text-sm opacity-75">View all activities</div>
            </div>
          </button>

          <button
            onClick={handleCreateMore}
            className="flex items-center justify-center space-x-3 p-4 bg-white border-2 border-green-200 text-green-700 rounded-xl hover:bg-green-50 transition-all"
          >
            <Star className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">Generate More</div>
              <div className="text-sm opacity-75">Create additional activities</div>
            </div>
          </button>
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Brain className="w-5 h-5 mr-2 text-blue-600" />
          Tips for Success
        </h4>
        
        <div className="space-y-3 text-sm">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-600 text-xs font-bold">1</span>
            </div>
            <div>
              <div className="font-medium text-gray-900">Start with easier activities</div>
              <div className="text-gray-600">Build confidence before moving to challenging ones</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-600 text-xs font-bold">2</span>
            </div>
            <div>
              <div className="font-medium text-gray-900">Take breaks when needed</div>
              <div className="text-gray-600">Learning is most effective with proper rest</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-purple-600 text-xs font-bold">3</span>
            </div>
            <div>
              <div className="font-medium text-gray-900">Celebrate progress</div>
              <div className="text-gray-600">Every completed activity is an achievement!</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}